import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Zap, Shield, TrendingUp, Users, CreditCard, Smartphone, 
  Globe, Menu, X, Check, Star, ChevronDown, Lock, Code, Headphones, 
  Clock, Activity, CheckCircle, MessageSquare
} from 'react-feather';
import ThemeToggle from '../components/ThemeToggle';
import LanguageToggle from '../components/LanguageToggle';
import BrandWordmark from '../components/BrandWordmark';
import AdsDisplay from '../components/AdsDisplay';
import { useLanguage } from '../context/LanguageContext';
import { getServiceColor, getServiceIconAsset } from '../utils/serviceIcons';
import '../styles/Landing.css';

export default function Landing() {
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? -1 : index);
  };

  const platforms = [
    { name: 'Instagram', key: 'instagram', desc: 'Followers, Likes, Views & Reels', tag: 'Most Popular' },
    { name: 'TikTok', key: 'tiktok', desc: 'Followers, Likes, Views & Shares', tag: 'Fast Delivery' },
    { name: 'YouTube', key: 'youtube', desc: 'Subscribers, High Retention Views & Likes', tag: 'Creator Choice' },
    { name: 'Telegram', key: 'telegram', desc: 'Channel Members, Post Views & Reactions', tag: 'Instant' },
    { name: 'Facebook', key: 'facebook', desc: 'Page Followers, Post Likes & Video Views', tag: 'Stable' },
    { name: 'WhatsApp & SMS', key: 'virtual_numbers', desc: 'Instant OTP SMS Verification Numbers', tag: 'Virtual Numbers' },
  ];

  const features = [
    {
      icon: <Zap size={24} />,
      title: 'Instant Automated Fulfillment',
      desc: 'Our direct high-speed API triggers order execution within seconds of placement. Watch your numbers climb in real-time.',
      badge: 'Real-Time'
    },
    {
      icon: <Shield size={24} />,
      title: '100% Safe & Password-Free',
      desc: 'We never ask for your account credentials or passwords. All delivery complies safely with major platform guidelines.',
      badge: 'Zero Risk'
    },
    {
      icon: <Smartphone size={24} />,
      title: 'Virtual Phone Numbers & SMS',
      desc: 'Rent disposable virtual phone numbers to receive SMS activation codes for WhatsApp, Telegram, Google, and 50+ services.',
      badge: 'Exclusive'
    },
    {
      icon: <CreditCard size={24} />,
      title: 'MoMo, Cards & Crypto Deposits',
      desc: 'Deposit instantly with MTN Mobile Money, Orange Money, Credit/Debit cards, or USDT & Bitcoin. Zero delayed verifications.',
      badge: 'Instant Funds'
    },
    {
      icon: <Code size={24} />,
      title: 'Reseller API Integration',
      desc: 'Power your own SMM panel or automate workflows with our robust, documented REST API. Full endpoint compatibility.',
      badge: 'Developers'
    },
    {
      icon: <Headphones size={24} />,
      title: '24/7 Human & Ticket Support',
      desc: 'Our dedicated customer success team is available around the clock via live ticket support and WhatsApp for rapid assistance.',
      badge: '24/7 Active'
    }
  ];

  const stats = [
    { value: '2,500,000+', label: 'Orders Completed', icon: <CheckCircle size={18} /> },
    { value: '50,000+', label: 'Active Creators & Agencies', icon: <Users size={18} /> },
    { value: '99.98%', label: 'Automated System Uptime', icon: <Activity size={18} /> },
    { value: '< 60s', label: 'Average Start Speed', icon: <Clock size={18} /> },
  ];

  const pricingPackages = [
    {
      title: 'Instagram Growth',
      category: 'Social Media',
      rate: '$1.49',
      unit: 'per 1,000',
      features: ['High-Quality Followers', 'Instant Automated Start', 'Refill Guarantee', 'Non-Drop Options Available'],
      badge: 'Best Seller',
      popular: true,
      to: '/register'
    },
    {
      title: 'TikTok Viral Boost',
      category: 'Video Engagement',
      rate: '$0.89',
      unit: 'per 1,000',
      features: ['High Retention Views', 'Viral Algorithm Boost', 'Real User Profiles', 'Instant Delivery < 2 mins'],
      badge: 'High Demand',
      popular: false,
      to: '/register'
    },
    {
      title: 'Telegram Members',
      category: 'Community',
      rate: '$1.99',
      unit: 'per 1,000',
      features: ['Channel & Group Members', 'Zero Drop Options', 'Post Views & Reactions', 'Global & Target Countries'],
      badge: 'Top Value',
      popular: false,
      to: '/register'
    },
    {
      title: 'Virtual SMS Numbers',
      category: 'Verification',
      rate: '$0.35',
      unit: 'per code',
      features: ['WhatsApp & Telegram OTP', 'USA, UK, France & 50+ Countries', 'Instant Code Delivery', 'Auto-Refund if Unused'],
      badge: 'Trending',
      popular: false,
      to: '/register'
    }
  ];

  const faqs = [
    {
      q: 'How fast do orders start after placing them?',
      a: 'Over 95% of our services start automatically within 30 to 60 seconds of order placement. You can monitor the real-time progress of your order right in your dashboard.'
    },
    {
      q: 'Do I need to provide my social media password?',
      a: 'Never. NiceBooster never asks for your social media password or account credentials. All we need is your public profile link or username to deliver services securely.'
    },
    {
      q: 'Which payment methods are supported for deposits?',
      a: 'We accept MTN Mobile Money, Orange Money, VISA/Mastercard cards, and Cryptocurrencies (USDT, BTC, ETH) with instant automated wallet crediting.'
    },
    {
      q: 'How do the Virtual Phone Numbers work?',
      a: 'You can generate a temporary or rental phone number from countries worldwide to receive SMS verification codes for apps like WhatsApp, Telegram, OpenAI, and more within seconds.'
    },
    {
      q: 'Can I resell your services on my own website?',
      a: 'Yes! We provide an automated high-performance REST API compatible with standard SMM panel software, allowing you to connect and resell our catalog at your own profit margins.'
    }
  ];

  const testimonials = [
    {
      name: 'Christian N.',
      role: 'Digital Agency Founder',
      country: 'Cameroon',
      text: 'NiceBooster has been a game-changer for our agency clients. The Mobile Money deposit option makes it effortless to fund our balance instantly, and order delivery is practically instantaneous.',
      rating: 5
    },
    {
      name: 'Amina K.',
      role: 'Content Creator & Influencer',
      country: 'Nigeria',
      text: 'The best SMM panel I have used. Clean interface, responsive support on WhatsApp, and the virtual number feature helped me verify multiple business accounts in minutes.',
      rating: 5
    },
    {
      name: 'David M.',
      role: 'E-commerce Brand Owner',
      country: 'Kenya',
      text: 'Wholesale prices that allow real profit margins. My orders start within seconds without drop-offs. Highly recommended for any serious digital marketer.',
      rating: 5
    }
  ];

  return (
    <>
      {/* Mobile Drawer Navigation */}
      <aside className={`landing-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <BrandWordmark className="landing-brand-mark" forceLightLogo />
          <button 
            className="sidebar-close-btn" 
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <a href="#features" className="sidebar-link" onClick={() => setSidebarOpen(false)}>{t('nav_features', 'Features')}</a>
          <a href="#platforms" className="sidebar-link" onClick={() => setSidebarOpen(false)}>{t('nav_platforms', 'Platforms')}</a>
          <a href="#how-it-works" className="sidebar-link" onClick={() => setSidebarOpen(false)}>{t('nav_how_it_works', 'How It Works')}</a>
          <a href="#pricing" className="sidebar-link" onClick={() => setSidebarOpen(false)}>{t('nav_pricing', 'Pricing')}</a>
          <a href="#faq" className="sidebar-link" onClick={() => setSidebarOpen(false)}>{t('nav_faq', 'FAQ')}</a>
        </nav>

        <div className="sidebar-theme-toggle">
          <span className="sidebar-toggle-label">{t('nav_language', 'Language')}</span>
          <LanguageToggle />
        </div>

        <div className="sidebar-theme-toggle">
          <span className="sidebar-toggle-label">Appearance</span>
          <ThemeToggle />
        </div>

        <div className="sidebar-buttons">
          <Link to="/login" className="sidebar-login" onClick={() => setSidebarOpen(false)}>{t('nav_login', 'Log In')}</Link>
          <Link to="/register" className="btn btn-primary sidebar-signup" onClick={() => setSidebarOpen(false)}>
            {t('nav_signup', 'Get Started Free')} <ArrowRight size={16} />
          </Link>
        </div>
      </aside>

      {/* Sidebar Backdrop */}
      {sidebarOpen && <div className="landing-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <div className="landing">
        {/* Modern Sticky Navigation */}
        <header className="landing-header">
          <div className="landing-nav-container">
            <div className="landing-brand-wrap">
              <BrandWordmark className="landing-brand-mark" forceLightLogo />
            </div>

            <nav className="landing-nav-links">
              <a href="#features" className="nav-link">{t('nav_features', 'Features')}</a>
              <a href="#platforms" className="nav-link">{t('nav_platforms', 'Platforms')}</a>
              <a href="#how-it-works" className="nav-link">{t('nav_how_it_works', 'How It Works')}</a>
              <a href="#pricing" className="nav-link">{t('nav_pricing', 'Pricing')}</a>
              <a href="#faq" className="nav-link">{t('nav_faq', 'FAQ')}</a>
            </nav>

            <div className="landing-nav-actions">
              <LanguageToggle />
              <div className="nav-theme-toggle">
                <ThemeToggle />
              </div>
              <Link to="/login" className="nav-link nav-login-link">{t('nav_login', 'Log In')}</Link>
              <Link to="/register" className="btn btn-primary nav-cta-btn">
                {t('nav_signup', 'Get Started')} <ArrowRight size={15} />
              </Link>
              <button 
                className="landing-menu-toggle" 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle navigation"
              >
                {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </header>

        {/* ── 1. Hero Section ── */}
        <section className="landing-hero-section">
          <div className="landing-container landing-hero-grid">
            <div className="landing-hero-content">
              <div className="landing-hero-badge">
                <Zap size={14} className="badge-zap" />
                <span>#1 Automated Growth & SMM Panel</span>
              </div>

              <h1 className="landing-hero-title">
                Accelerate Your Social Growth at <span className="text-highlight">Wholesale Rates</span>
              </h1>

              <p className="landing-hero-subtitle">
                The all-in-one automated platform for creators, agencies, and businesses. Boost followers, likes, views across all major networks, and get instant virtual phone numbers for SMS verification.
              </p>

              <div className="landing-hero-actions">
                <Link to="/register" className="btn btn-primary btn-hero-cta">
                  <span>Get Started Free</span>
                  <ArrowRight size={18} />
                </Link>
                <Link to="/services" className="btn btn-secondary btn-hero-secondary">
                  <span>View Services Catalog</span>
                </Link>
              </div>

              <div className="landing-trust-row">
                <div className="trust-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} fill="#facc15" color="#facc15" />
                  ))}
                  <span className="trust-rating">4.9 / 5</span>
                </div>
                <div className="trust-divider" />
                <span className="trust-note">Trusted by 50,000+ creators • MoMo & Crypto accepted</span>
              </div>
            </div>

            {/* Interactive Simulated Live Dashboard Preview Card */}
            <div className="landing-hero-preview-col">
              <div className="hero-preview-card">
                <div className="preview-top-bar">
                  <div className="preview-status-pill">
                    <span className="preview-live-dot" />
                    <span>Live Order Network</span>
                  </div>
                  <div className="preview-latency-badge">
                    <Activity size={13} /> 12ms ping
                  </div>
                </div>

                <div className="preview-stats-grid">
                  <div className="preview-stat-card">
                    <div className="preview-stat-icon ig-bg">
                      <TrendingUp size={16} />
                    </div>
                    <div className="preview-stat-info">
                      <span className="preview-stat-label">Delivered Today</span>
                      <span className="preview-stat-val">+148,920</span>
                    </div>
                  </div>

                  <div className="preview-stat-card">
                    <div className="preview-stat-icon vn-bg">
                      <Smartphone size={16} />
                    </div>
                    <div className="preview-stat-info">
                      <span className="preview-stat-label">SMS Received</span>
                      <span className="preview-stat-val">3,410 Codes</span>
                    </div>
                  </div>
                </div>

                <div className="preview-live-feed">
                  <div className="feed-header">
                    <span>Recent Activity</span>
                    <span className="feed-live-tag">REAL-TIME</span>
                  </div>

                  <div className="feed-item">
                    <div className="feed-item-icon check-green">
                      <Check size={13} />
                    </div>
                    <div className="feed-item-body">
                      <span className="feed-item-title">Instagram Followers (HQ Real)</span>
                      <span className="feed-item-meta">10,000 Delivered • 45s ago</span>
                    </div>
                    <span className="feed-item-status completed">Done</span>
                  </div>

                  <div className="feed-item">
                    <div className="feed-item-icon check-green">
                      <Check size={13} />
                    </div>
                    <div className="feed-item-body">
                      <span className="feed-item-title">WhatsApp Virtual SMS Code</span>
                      <span className="feed-item-meta">+1 (202) • Code: [839-102]</span>
                    </div>
                    <span className="feed-item-status completed">Received</span>
                  </div>

                  <div className="feed-item">
                    <div className="feed-item-icon pulse-blue">
                      <Zap size={13} />
                    </div>
                    <div className="feed-item-body">
                      <span className="feed-item-title">TikTok Video Views & Likes</span>
                      <span className="feed-item-meta">50,000 In Progress • 98%</span>
                    </div>
                    <span className="feed-item-status in-progress">Running</span>
                  </div>
                </div>

                <div className="preview-footer-balance">
                  <div className="balance-info">
                    <span className="balance-sub">Available Wallet Balance</span>
                    <span className="balance-amount">$245.80</span>
                  </div>
                  <Link to="/register" className="preview-deposit-btn">
                    Instant MoMo Top Up →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. Real Metrics & Stats Ribbon ── */}
        <section className="landing-stats-ribbon">
          <div className="landing-container stats-grid">
            {stats.map((s, idx) => (
              <div key={idx} className="stat-box">
                <div className="stat-icon-wrap">{s.icon}</div>
                <div className="stat-text-wrap">
                  <span className="stat-number">{s.value}</span>
                  <span className="stat-name">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Ad 1 - After Stats */}
        <AdsDisplay adSlot="banner-728x90" className="banner" />

        {/* ── 3. Supported Platforms ── */}
        <section id="platforms" className="landing-platforms-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Supported Networks</span>
              <h2 className="section-title">Grow Across Every Major Platform</h2>
              <p className="section-subtitle">
                From organic social engagement to instant SMS verification, we provide tools for your complete digital footprint.
              </p>
            </div>

            <div className="landing-platforms-grid">
              {platforms.map((p) => {
                const iconAsset = getServiceIconAsset(p.key);
                const color = getServiceColor(p.key);
                const isTiktok = p.key === 'tiktok';

                return (
                  <div key={p.key} className="platform-card">
                    <div className="platform-card-header">
                      <div 
                        className="platform-card-icon"
                        style={{
                          background: isTiktok ? 'linear-gradient(135deg, #0f172a 0%, #111827 100%)' : `${color}16`,
                          borderColor: isTiktok ? 'rgba(15, 23, 42, 0.55)' : `${color}33`,
                          color
                        }}
                      >
                        {iconAsset ? (
                          <img 
                            src={iconAsset} 
                            alt={p.name} 
                            style={{ 
                              width: 28, 
                              height: 28, 
                              filter: isTiktok ? 'drop-shadow(0 2px 8px rgba(34,211,238,0.3))' : 'none' 
                            }} 
                          />
                        ) : (
                          <Globe size={24} />
                        )}
                      </div>
                      <span className="platform-tag">{p.tag}</span>
                    </div>

                    <h3 className="platform-name">{p.name}</h3>
                    <p className="platform-desc">{p.desc}</p>
                    <Link to="/services" className="platform-link">
                      Explore Services <ArrowRight size={14} />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── 4. Core Features / Why NiceBooster ── */}
        <section id="features" className="landing-features-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Why Choose Us</span>
              <h2 className="section-title">Built for Performance, Speed & Safety</h2>
              <p className="section-subtitle">
                Everything you need to scale your online brand effortlessly, backed by reliable technology.
              </p>
            </div>

            <div className="landing-features-grid">
              {features.map((f, i) => (
                <div key={i} className="feature-modern-card">
                  <div className="feature-top">
                    <div className="feature-icon-bubble">{f.icon}</div>
                    <span className="feature-badge">{f.badge}</span>
                  </div>
                  <h3 className="feature-card-title">{f.title}</h3>
                  <p className="feature-card-desc">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ad 2 - Mid Page Banner */}
        <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

        {/* ── 5. How It Works ── */}
        <section id="how-it-works" className="landing-how-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Simple Process</span>
              <h2 className="section-title">Start Growing in 3 Simple Steps</h2>
              <p className="section-subtitle">
                From setup to execution, start seeing results in less than two minutes.
              </p>
            </div>

            <div className="landing-steps-row">
              <div className="step-modern-card">
                <div className="step-number-wrap">
                  <span className="step-num">01</span>
                </div>
                <h3 className="step-title">Create Free Account</h3>
                <p className="step-desc">
                  Sign up in 30 seconds with just an email address. No credit card or password commitments required.
                </p>
              </div>

              <div className="step-connector">
                <ArrowRight size={22} />
              </div>

              <div className="step-modern-card">
                <div className="step-number-wrap">
                  <span className="step-num">02</span>
                </div>
                <h3 className="step-title">Add Funds Instantly</h3>
                <p className="step-desc">
                  Top up your wallet easily using MTN Mobile Money, Orange Money, Card, or USDT Crypto with instant confirmation.
                </p>
              </div>

              <div className="step-connector">
                <ArrowRight size={22} />
              </div>

              <div className="step-modern-card">
                <div className="step-number-wrap">
                  <span className="step-num">03</span>
                </div>
                <h3 className="step-title">Choose Service & Boost</h3>
                <p className="step-desc">
                  Select your platform, paste your link, specify the quantity, and watch our automated engine deliver.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. Transparent Wholesale Pricing ── */}
        <section id="pricing" className="landing-pricing-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Direct Wholesale Rates</span>
              <h2 className="section-title">Transparent & Competitive Pricing</h2>
              <p className="section-subtitle">
                Enjoy wholesale tier rates whether you order 100 followers or 1,000,000 views.
              </p>
            </div>

            <div className="landing-pricing-grid">
              {pricingPackages.map((pkg, i) => (
                <div key={i} className={`pricing-modern-card ${pkg.popular ? 'featured' : ''}`}>
                  {pkg.popular && <div className="pricing-badge-popular">Most Popular</div>}
                  <div className="pricing-header">
                    <span className="pricing-category">{pkg.category}</span>
                    <h3 className="pricing-title">{pkg.title}</h3>
                    <div className="pricing-rate-row">
                      <span className="pricing-price">{pkg.rate}</span>
                      <span className="pricing-unit">/ {pkg.unit}</span>
                    </div>
                  </div>

                  <ul className="pricing-features-list">
                    {pkg.features.map((feat, fIdx) => (
                      <li key={fIdx}>
                        <Check size={16} className="pricing-check-icon" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={pkg.to} className="btn btn-primary pricing-cta-btn">
                    Order Now <ArrowRight size={16} />
                  </Link>
                </div>
              ))}
            </div>

            <div className="pricing-guarantee-strip">
              <Shield size={18} />
              <span>All orders protected by our automated delivery monitor & instant wallet refunds if unfulfilled.</span>
            </div>
          </div>
        </section>

        {/* ── 7. Growth Guides ── */}
        <section className="landing-guides-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Educational Guides</span>
              <h2 className="section-title">Expert Social Growth Insights</h2>
              <p className="section-subtitle">
                Learn best practices to maximize organic reach and build long-term social authority.
              </p>
            </div>

            <div className="landing-guides-grid">
              {[
                {
                  title: 'Buy Instagram Followers in Africa',
                  text: 'Understand follower quality, refill types, and how to pair followers with real engagement.',
                  to: '/guides/instagram-followers',
                  key: 'instagram',
                },
                {
                  title: 'Buy TikTok Views and Engagement',
                  text: 'Learn when views help, when they do not, and how to balance them with likes and profile growth.',
                  to: '/guides/tiktok-views',
                  key: 'tiktok',
                },
                {
                  title: 'Buy Telegram Members',
                  text: 'See how channel members, views, and reactions work together for stronger Telegram growth.',
                  to: '/guides/telegram-members',
                  key: 'telegram',
                },
                {
                  title: 'Website Traffic Explained',
                  text: 'Read the difference between website traffic, SEO traffic, and social traffic services.',
                  to: '/guides/website-traffic-explained',
                  key: 'website',
                },
              ].map((guide) => {
                const iconAsset = getServiceIconAsset(guide.key);
                const color = getServiceColor(guide.key);
                const isTiktokGuide = guide.key === 'tiktok';

                return (
                  <Link key={guide.to} to={guide.to} className="landing-guide-modern-card">
                    <div
                      className="landing-guide-icon-wrap"
                      style={{
                        background: isTiktokGuide
                          ? 'linear-gradient(135deg, #0f172a 0%, #111827 100%)'
                          : `${color}14`,
                        borderColor: isTiktokGuide ? 'rgba(15, 23, 42, 0.55)' : `${color}28`,
                        color,
                      }}
                    >
                      {iconAsset && (
                        <img
                          src={iconAsset}
                          alt=""
                          style={{
                            width: 24,
                            height: 24,
                            filter: isTiktokGuide ? 'drop-shadow(0 2px 8px rgba(34,211,238,0.28))' : 'none',
                          }}
                        />
                      )}
                    </div>
                    <div className="guide-content">
                      <h3 className="guide-title">{guide.title}</h3>
                      <p className="guide-text">{guide.text}</p>
                      <span className="guide-action">
                        Read full guide <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── 8. Creator Reviews & Testimonials ── */}
        <section className="landing-testimonials-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">Client Reviews</span>
              <h2 className="section-title">Loved by Thousands of Creators</h2>
              <p className="section-subtitle">
                Here is what digital agencies, creators, and marketers have to say about NiceBooster.
              </p>
            </div>

            <div className="landing-testimonials-grid">
              {testimonials.map((t, idx) => (
                <div key={idx} className="testimonial-card">
                  <div className="testimonial-stars">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#facc15" color="#facc15" />
                    ))}
                  </div>
                  <p className="testimonial-quote">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">{t.name.charAt(0)}</div>
                    <div className="author-meta">
                      <h4 className="author-name">{t.name}</h4>
                      <span className="author-role">{t.role} • {t.country}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. Frequently Asked Questions ── */}
        <section id="faq" className="landing-faq-section">
          <div className="landing-container">
            <div className="section-head text-center">
              <span className="section-tag">FAQ</span>
              <h2 className="section-title">Frequently Asked Questions</h2>
              <p className="section-subtitle">
                Everything you need to know about our services, payments, and delivery.
              </p>
            </div>

            <div className="landing-faq-accordion">
              {faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={idx} className={`faq-item ${isOpen ? 'active' : ''}`}>
                    <button 
                      className="faq-question-btn" 
                      onClick={() => toggleFaq(idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.q}</span>
                      <ChevronDown size={18} className={`faq-chevron ${isOpen ? 'open' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="faq-answer-panel">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Ad 3 - Bottom Banner */}
        <AdsDisplay adSlot="banner-320x50" className="banner" />

        {/* ── 10. High-Converting CTA Banner ── */}
        <section className="landing-final-cta-section">
          <div className="landing-container">
            <div className="final-cta-card">
              <div className="final-cta-content">
                <span className="final-cta-pill">Ready to start?</span>
                <h2 className="final-cta-title">Supercharge Your Social Presence Today</h2>
                <p className="final-cta-subtitle">
                  Join over 50,000 creators, digital brands, and marketing agencies growing effortlessly with NiceBooster.
                </p>
                <div className="final-cta-actions">
                  <Link to="/register" className="btn btn-primary btn-cta-primary">
                    Create Free Account <ArrowRight size={18} />
                  </Link>
                  <a href="https://wa.me/237688656076" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-cta-whatsapp">
                    <MessageSquare size={17} /> Talk to Support
                  </a>
                </div>
                <div className="final-cta-perks">
                  <span><Check size={14} /> Instant automated start</span>
                  <span><Check size={14} /> No password required</span>
                  <span><Check size={14} /> MTN / Orange MoMo & Crypto</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 11. Footer ── */}
        <footer className="landing-footer">
          <div className="landing-container footer-layout">
            <div className="footer-col-brand">
              <BrandWordmark className="landing-brand-mark" forceLightLogo />
              <p className="footer-bio">
                The premier automated social media marketing panel and virtual phone number verification hub. Fast, secure, and affordable.
              </p>
              <div className="footer-support-pill">
                <span className="live-pulse" />
                <span>24/7 Live System Operation</span>
              </div>
            </div>

            <div className="footer-col-links">
              <h4 className="footer-col-title">Navigation</h4>
              <ul>
                <li><Link to="/services">Services & Pricing</Link></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>

            <div className="footer-col-links">
              <h4 className="footer-col-title">Account</h4>
              <ul>
                <li><Link to="/login">Sign In</Link></li>
                <li><Link to="/register">Create Account</Link></li>
                <li><Link to="/dashboard/deposit">Add Funds</Link></li>
                <li><Link to="/dashboard/virtual-numbers">Virtual Numbers</Link></li>
              </ul>
            </div>

            <div className="footer-col-links">
              <h4 className="footer-col-title">Support & Legal</h4>
              <ul>
                <li><a href="https://wa.me/237688656076" target="_blank" rel="noopener noreferrer">WhatsApp Support</a></li>
                <li><a href="mailto:support@nicebooster.site">Email Support</a></li>
                <li><Link to="/terms-of-service">Terms of Service</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/refund-policy">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="landing-container footer-bottom-bar">
            <p className="footer-copyright">&copy; {new Date().getFullYear()} NiceBooster. All rights reserved.</p>
            <div className="footer-legal-tags">
              <span>Mobile Money Supported</span>
              <span>•</span>
              <span>Encrypted SSL 256-Bit</span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
